import React, { ReactNode } from 'react'

import { AssetFrame } from '../../components/primitives/AssetFrame/AssetFrame'
import { TextClamp } from '../../components/text/TextClamp'
import { DECK_CHART_PRIMARY_COLOR } from '../../foundations/chart-colors'
import { formatPodiumScore, formatPodiumShare } from '../../slides/podium-presentation'
import { ChartLabel } from '../_shared/ChartLabel'

import { RankItem } from '@digital-twins/slide-schemas/generated/plot-types'
import { StoredPlotPayload } from '../contract'

/**
 * The ranking-bars plot (plots plan I): ranked rows, each a rank badge, an
 * optional picture, the name, the figure and a bar on the producer's scale.
 * One component for the deck's ranking slide (`size: 'slide'`) and the
 * summary card and survey tab (`size: 'card'`).
 *
 * It draws Python's stamps and computes none: the order and the rank on each
 * item, the winner (`topIds`) and the tie, the decimals and the bar scale all
 * come from the payload. A tie removes the crown and shows "too close to
 * call"; every tied top item is still highlighted. Chrome it has no label for
 * is omitted, never written in English.
 */

export type RankingBarsData = StoredPlotPayload<'ranking-bars'>['data']

export interface RankingBarsLabelArgs {
  item: RankItem
  /** The item's index in the drawn ranking (the deck's edit field path). */
  index: number
  maxLines: number
  style: React.CSSProperties
}

export interface RankingBarsPlotProps {
  data: RankingBarsData
  size?: 'slide' | 'card'
  /** `top`: the stamped winners draw in the primary colour, the rest soft. `none`: every bar primary. */
  highlight?: 'top' | 'none'
  renderMode?: 'interactive' | 'static'
  /** The selected series id; the top-level `items` when absent or unknown. */
  seriesId?: string | null
  /**
   * The deck passes its editable text; everyone else gets the shared chart label
   * (plots plan LB, decision 24): whole, or an ellipsis and, on an interactive
   * surface, the (i) with the whole name.
   */
  renderLabel?: (args: RankingBarsLabelArgs) => ReactNode
  /** Legacy stored payloads print today's figure string; new payloads never set it. */
  formatValue?: (item: RankItem) => string | null
  /** Card only: the caller's label formatter (the frontend passes its `formatLabel`). */
  formatItemLabel?: (label: string) => string
  /** Card only: the heading drawn beside the tie chip (what the figure means). */
  heading?: string | null
  /** What an empty ranking says. */
  emptyLabel?: string
  testIdPrefix?: string
}

const GRAY = 'var(--slide-text-muted, #737373)'
const DARK = 'var(--slide-cover-bg)'

/**
 * What respondents read of an imageless row (`respondentCopy`), drawn whole
 * under its name: a text ad's row shows its copy, as its board tile does
 * (`PodiumPlotView`). A row with a picture shows the picture only.
 */
const copyOf = (item: RankItem): string | null => (item.imageUrl ? null : item.respondentCopy?.trim() || null)
const SOFT_BAR = 'var(--slide-chart-6)'
const SLIDE_TRACK = 'var(--slide-neutral-light)'
const SLIDE_BADGES: Record<number, { bg: string; fg: string }> = {
  1: { bg: DECK_CHART_PRIMARY_COLOR, fg: '#FFFFFF' },
  2: { bg: 'var(--slide-highlight-soft)', fg: DARK },
  3: { bg: 'var(--slide-accent-soft)', fg: DARK },
}
const SLIDE_DEFAULT_BADGE = { bg: 'var(--slide-neutral-light)', fg: 'var(--slide-text-muted)' }
// A tied first place loses the crown's fill but keeps its place.
const SLIDE_TIED_FIRST_BADGE = { bg: 'var(--slide-highlight-soft)', fg: DARK }
const PERCENT_UNITS = new Set(['percent', 'percent_0_100'])
const SHARE_FIGURES = new Set(['share', 'pick_share'])

export function fillRankTemplate(template: string | undefined, values: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match))
}

/** The figure as Python rounded it, at the stamped decimals, `%` on a share. */
export function formatRankFigure(value: number, data: Pick<RankingBarsData, 'decimals' | 'unit'>): string | null {
  return PERCENT_UNITS.has(data.unit) ? formatPodiumShare(value, data.decimals) : formatPodiumScore(value, data.decimals)
}

/** The bar's share of the track on the stamped scale, 0-100. */
export function rankBarPercent(value: number, scale: RankingBarsData['scale']): number {
  if (!scale || !(scale.max > scale.min)) {
    return 0
  }
  const share = ((value - scale.min) / (scale.max - scale.min)) * 100
  return Number.isFinite(share) ? Math.max(0, Math.min(100, share)) : 0
}

function scaleEnd(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10)
}

interface ActiveRanking {
  items: Array<RankItem>
  topIds: Array<string>
  tie: boolean
}

function activeRanking(data: RankingBarsData, seriesId?: string | null): ActiveRanking {
  const series = seriesId ? (data.series ?? []).find((entry) => entry.id === seriesId) : undefined
  if (series) {
    return { items: series.items, topIds: series.topIds, tie: series.tie }
  }
  return { items: data.items, topIds: data.topIds, tie: data.tie }
}

export function RankingBarsPlot(props: RankingBarsPlotProps) {
  const {
    data,
    size = 'card',
    highlight = 'top',
    seriesId,
    renderMode,
    renderLabel,
    formatValue,
    formatItemLabel,
    heading,
    emptyLabel,
    testIdPrefix = 'ranking-bars',
  } = props

  const { items, topIds, tie } = activeRanking(data, seriesId)
  const top = new Set(topIds)
  const labels = data.labels
  const figureOf = (item: RankItem) => formatValue?.(item) ?? formatRankFigure(item.value, data) ?? ''
  const barColor = (item: RankItem) => {
    if (item.isEscape) {
      return GRAY
    }
    if (highlight === 'none') {
      return DECK_CHART_PRIMARY_COLOR
    }
    return top.has(item.id) ? DECK_CHART_PRIMARY_COLOR : SOFT_BAR
  }
  // The crown: a first place that is the one untied winner.
  const isCrowned = (item: RankItem) => !tie && top.has(item.id) && item.rank === 1
  const rankText = (item: RankItem) => fillRankTemplate(labels.plot_rank_n, { rank: item.rank }) ?? String(item.rank)

  const showsScale = data.scale && !SHARE_FIGURES.has(data.figure)
  const scaleText = showsScale && data.scale
    ? fillRankTemplate(labels.plot_score_range, { low: scaleEnd(data.scale.min), high: scaleEnd(data.scale.max) })
    : null
  const truncationText = data.truncation
    ? fillRankTemplate(labels.n_of_total_shown, { shown: data.truncation.shown, total: data.truncation.total })
    : null
  const tieText = tie ? (labels.too_close_to_call ?? null) : null

  if (items.length === 0) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, color: GRAY }}
      >
        {emptyLabel ?? ''}
      </div>
    )
  }

  const caption = scaleText || truncationText || (size === 'slide' && tieText) ? (
    <div
      data-testid={`${testIdPrefix}-caption`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        fontSize: size === 'slide' ? 13 : 12,
        color: GRAY,
        marginTop: size === 'slide' ? 12 : 8,
        flexShrink: 0,
      }}
    >
      {size === 'slide' && tieText ? (
        <span
          data-testid={`${testIdPrefix}-tie`}
          style={{ borderRadius: 999, padding: '2px 10px', background: 'var(--slide-accent-soft)', color: DARK, fontWeight: 600 }}
        >
          {tieText}
        </span>
      ) : null}
      {scaleText ? <span data-testid={`${testIdPrefix}-score-range`}>{scaleText}</span> : null}
      {truncationText ? <span data-testid={`${testIdPrefix}-truncation`}>{truncationText}</span> : null}
    </div>
  ) : null

  if (size === 'slide') {
    return (
      <div data-testid={testIdPrefix} style={{ display: 'flex', flexDirection: 'column', flex: '1 0 auto' }}>
        <div
          data-testid="ranking-slide-chart"
          style={{ display: 'flex', flexDirection: 'column', gap: items.length > 6 ? 16 : 24, flex: '1 0 auto' }}
        >
          {items.map((item, index) => {
            const badge = item.rank === 1 && !isCrowned(item) ? SLIDE_TIED_FIRST_BADGE : (SLIDE_BADGES[item.rank] ?? SLIDE_DEFAULT_BADGE)
            const labelStyle: React.CSSProperties = { fontSize: 16, color: DARK, minWidth: 0, flex: 1 }
            return (
              <div
                key={item.id}
                data-testid="ranking-bar-row"
                data-top={top.has(item.id) ? 'true' : 'false'}
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    data-testid="ranking-badge"
                    data-crowned={isCrowned(item) ? 'true' : 'false'}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 4px',
                      boxSizing: 'border-box',
                      borderRadius: 999,
                      background: badge.bg,
                      color: badge.fg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {item.rank}
                  </div>
                  {item.imageUrl ? (
                    <AssetFrame
                      url={item.imageUrl}
                      kind={item.previewKind ?? 'image'}
                      alt={item.label}
                      radius={4}
                      data-testid="ranking-row-thumbnail"
                      style={{ width: 28, height: 28, flexShrink: 0 }}
                    />
                  ) : null}
                  {renderLabel ? (
                    renderLabel({ item, index, maxLines: 1, style: labelStyle })
                  ) : (
                    <ChartLabel
                      data-testid="ranking-bar-label"
                      text={item.label}
                      hintIndex={index}
                      minLines={1}
                      maxLines={1}
                      lineHeight="1.2"
                      renderMode={renderMode}
                      style={labelStyle}
                    />
                  )}
                  <TextClamp
                    minLines={1}
                    maxLines={1}
                    lineHeight="1.2"
                    style={{ fontSize: 18, fontWeight: 600, color: DARK, textAlign: 'right', width: 72, flexShrink: 0 }}
                  >
                    {figureOf(item)}
                  </TextClamp>
                </div>
                {copyOf(item) ? (
                  <div
                    data-testid="ranking-row-copy"
                    style={{ marginLeft: 40, fontSize: 14, lineHeight: 1.3, color: DARK, opacity: 0.75 }}
                  >
                    {`\u201c${copyOf(item)}\u201d`}
                  </div>
                ) : null}
                <div style={{ marginLeft: 40, height: 18, borderRadius: 4, background: SLIDE_TRACK, overflow: 'hidden' }}>
                  <div
                    data-testid="ranking-bar-fill"
                    style={{
                      height: '100%',
                      width: `${Math.max(2, rankBarPercent(item.value, data.scale))}%`,
                      borderRadius: 4,
                      background: barColor(item),
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
        {caption}
      </div>
    )
  }

  const format = formatItemLabel ?? ((label: string) => label)
  const ranked = items.filter((item) => !item.isEscape)
  const escapes = items.filter((item) => item.isEscape)

  return (
    <div className="w-full" data-testid={testIdPrefix}>
      {(heading || tieText) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-sm font-medium text-muted-foreground">{heading ?? ''}</span>
          {tieText && (
            <span
              data-testid={`${testIdPrefix}-tie`}
              className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-muted/60 text-foreground/70"
            >
              {tieText}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-4">
        {ranked.map((item, index) => {
          const label = format(item.label)
          const crowned = isCrowned(item)
          return (
            <div
              key={item.id}
              data-testid={`${testIdPrefix}-row-${index}`}
              data-top={top.has(item.id) ? 'true' : 'false'}
              data-escape="false"
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center gap-3">
                <span
                  data-testid={`${testIdPrefix}-rank-${index}`}
                  data-crowned={crowned ? 'true' : 'false'}
                  className={`text-[11px] font-bold flex-shrink-0 tabular-nums whitespace-nowrap rounded-full px-2 py-0.5 ${crowned ? 'text-white' : 'text-muted-foreground'}`}
                  style={crowned ? { backgroundColor: DECK_CHART_PRIMARY_COLOR } : undefined}
                >
                  {rankText(item)}
                </span>
                {item.imageUrl ? (
                  <AssetFrame
                    url={item.imageUrl}
                    kind={item.previewKind ?? 'image'}
                    alt={label}
                    radius={8}
                    data-testid={`${testIdPrefix}-image-${index}`}
                    className="w-9 h-9 flex-shrink-0 border border-border/40"
                  />
                ) : (
                  // The item letter, else the name's first letter: today's card tile.
                  <span
                    data-testid={`${testIdPrefix}-tile-${index}`}
                    className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold bg-muted/40 text-foreground/70"
                  >
                    {item.tag ?? (label.trim().charAt(0).toUpperCase() || '?')}
                  </span>
                )}
                <ChartLabel
                  data-testid={`${testIdPrefix}-label-${index}`}
                  text={label}
                  hintIndex={index}
                  maxLines={2}
                  renderMode={renderMode}
                  className="text-sm leading-snug flex-1 min-w-0 font-normal text-foreground"
                />
                <span data-testid={`${testIdPrefix}-value-${index}`} className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">
                  {figureOf(item)}
                </span>
              </div>
              {copyOf(item) ? (
                <span
                  data-testid={`${testIdPrefix}-copy-${index}`}
                  className="text-xs leading-snug text-muted-foreground"
                >
                  {`\u201c${copyOf(item)}\u201d`}
                </span>
              ) : null}
              <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                <div
                  data-testid={`${testIdPrefix}-fill-${index}`}
                  className="h-full rounded-full"
                  style={{ width: `${rankBarPercent(item.value, data.scale)}%`, backgroundColor: barColor(item) }}
                />
              </div>
            </div>
          )
        })}
        {escapes.length > 0 && (
          <div
            data-testid={`${testIdPrefix}-escapes`}
            className={`flex flex-col gap-4 opacity-60${ranked.length > 0 ? ' border-t border-border/50 pt-4' : ''}`}
          >
            {escapes.map((item, index) => {
              const label = format(item.label)
              return (
                <div key={item.id} data-testid={`${testIdPrefix}-escape-${index}`} data-escape="true" className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-3">
                    <ChartLabel
                      data-testid={`${testIdPrefix}-escape-label-${index}`}
                      text={label}
                      hintIndex={ranked.length + index}
                      maxLines={2}
                      renderMode={renderMode}
                      className="text-sm leading-snug flex-1 min-w-0 font-normal text-foreground"
                    />
                    <span className="text-sm font-normal text-foreground tabular-nums flex-shrink-0">{figureOf(item)}</span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full overflow-hidden h-2">
                    <div className="h-full rounded-full" style={{ width: `${rankBarPercent(item.value, data.scale)}%`, backgroundColor: GRAY }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {caption}
    </div>
  )
}
